// "Revenir à la réunion" (COMX-FE-02): a slim bar at the top of the pages of the app while a
// meeting the person joined is still live and they are no longer in its room (they navigated
// away). It shows the title and leads back to the room, or forgets the meeting ("Quitter"). The
// API is asked whether the meeting is live; nothing about it is trusted from the browser.
import { ApiError } from '@batiflow/client-core';
import { isolate } from '@batiflow/ui';
import { Button, __css, tokens } from '@fluentui/react-components';
import { Video20Filled } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { activeMeetingStore, useActiveMeeting } from './active-meeting';
import { useMeeting, useMeetingEvents } from './api';

const useStyles = __css({"bar":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f778t4r","qttt6h":"f1szrjqy","B7ck84d":"f1ewtqcl","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","De3pzq":"f16xkysk","sj55zd":"f19n0e5","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"fh32zs"},"icon":{"Bnnss6s":"fi64zpg","sj55zd":"f16muhyy"},"text":{"Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Btd35i7":"fjksvth"},"actions":{"mc9l5x":"f22iagw","i8kkvl":"fsnqrgy"}});

function Bar({ id, title }: { id: string; title: string }) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const navigate = useNavigate();
  const meeting = useMeeting(id);
  useMeetingEvents(id);
  const gone =
    meeting.data?.status === 'ended' ||
    (meeting.error instanceof ApiError &&
      (meeting.error.status === 403 || meeting.error.status === 404));
  useEffect(() => {
    if (gone) activeMeetingStore.getState().clear();
  }, [gone]);
  // Shown only while the API says the meeting is live.
  if (meeting.data?.status !== 'live') return null;
  return (
    <section className={styles.bar} aria-label={t('returnBar.label')}>
      <Video20Filled className={styles.icon} aria-hidden="true" />
      <p className={styles.text}>
        {t('returnBar.text', { title: isolate(meeting.data.title || title) })}
      </p>
      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="small"
          onClick={() => {
            void navigate({ to: '/meetings/$meetingId', params: { meetingId: id } });
          }}
        >
          {t('returnBar.back')}
        </Button>
        <Button
          size="small"
          onClick={() => {
            activeMeetingStore.getState().clear();
          }}
        >
          {t('returnBar.leave')}
        </Button>
      </div>
    </section>
  );
}

/** Mounted above the page content of the shells; renders nothing unless a meeting is waiting. */
export function ReturnToMeetingBar() {
  const meeting = useActiveMeeting();
  if (!meeting) return null;
  return <Bar key={meeting.id} id={meeting.id} title={meeting.title} />;
}


import "/opt/batiflow/app/apps/web/src/features/meetings/return-bar.griffel.css";